import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger, SidebarInset } from './ui/sidebar';
import AppSidebar from './Sidebar';
import { UserButton } from '@clerk/clerk-react';
import { ThemeToggle } from '../contexts/ThemeContext';
import { ChevronRight, Home, Search, Command } from 'lucide-react';
import { menuConfig } from '../config/menuConfig';
import { CommandSearch } from './CommandSearch';
import { supabase } from '../services/supabaseClient.js';

const ZOMBIE_NOTIFICATION_STORAGE_KEY = 'zombie-subscriptions-last-notified-run-id';
const ZOMBIE_NOTIFICATION_MAX_AGE_MS = 3 * 60 * 60 * 1000;

// ─────────────────────────────────────────────────────────────
// Hook: construye el breadcrumb a partir del pathname + menuConfig
// ─────────────────────────────────────────────────────────────
function useBreadcrumbs() {
  const location = useLocation();
  const path = location.pathname;

  for (const section of menuConfig.sections) {
    if (!section) continue;

    if (section.type === 'single' && section.path) {
      const match =
        path === section.path ||
        (section.path !== '/' && path.startsWith(section.path));
      if (match) {
        return [{ title: section.title, path: section.path }];
      }
    }

    if (section.type === 'group' && section.items) {
      // Busca el ítem más específico (más largo que haga match)
      let bestMatch = null;
      for (const item of section.items) {
        if (!item?.path) continue;
        if (path.startsWith(item.path)) {
          if (!bestMatch || item.path.length > bestMatch.path.length) {
            bestMatch = item;
          }
        }
      }
      if (bestMatch) {
        return [
          { title: section.title, path: null },
          { title: bestMatch.title, path: bestMatch.path },
        ];
      }
    }
  }

  return [{ title: 'Dashboard', path: '/' }];
}

// ─────────────────────────────────────────────────────────────
// Componente Breadcrumb
// ─────────────────────────────────────────────────────────────
function Breadcrumb() {
  const crumbs = useBreadcrumbs();

  return (
    <nav className="flex items-center gap-1 text-sm min-w-0" aria-label="Breadcrumb">
      <Link
        to="/"
        className="text-slate-400 hover:text-[#007D6E] transition-colors flex-shrink-0"
        title="Dashboard"
      >
        <Home className="h-3.5 w-3.5" />
      </Link>

      {crumbs.map((crumb, idx) => (
        <React.Fragment key={`${crumb.title}-${idx}`}>
          <ChevronRight className="yool-breadcrumb-sep h-3.5 w-3.5 flex-shrink-0" />
          {crumb.path && idx < crumbs.length - 1 ? (
            <Link
              to={crumb.path}
              className="text-slate-500 dark:text-slate-400 hover:text-[#007D6E] transition-colors truncate"
            >
              {crumb.title}
            </Link>
          ) : crumb.path && idx === crumbs.length - 1 ? (
            <span className="font-medium text-slate-800 dark:text-slate-100 truncate">
              {crumb.title}
            </span>
          ) : (
            <span className="text-slate-500 dark:text-slate-400 truncate">
              {crumb.title}
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

// ─────────────────────────────────────────────────────────────
// Layout principal
// ─────────────────────────────────────────────────────────────
export default function Layout({ children }) {
  const [searchOpen, setSearchOpen] = useState(false);

  // ⌘K global shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const closeSearch = useCallback(() => setSearchOpen(false), []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return undefined;
    }

    let cancelled = false;

    const checkZombieSubscriptionAlerts = async () => {
      try {
        const { data, error } = await supabase
          .from('cron_run_logs')
          .select('run_id, started_at, metrics')
          .eq('job_name', 'zombie-subscriptions')
          .eq('status', 'success')
          .order('started_at', { ascending: false })
          .limit(1);

        if (cancelled || error || !Array.isArray(data) || !data.length) {
          return;
        }

        const latestRun = data[0];
        const latestRunId = latestRun?.run_id || '';
        if (!latestRunId) {
          return;
        }

        const alreadyNotifiedRunId = window.localStorage.getItem(ZOMBIE_NOTIFICATION_STORAGE_KEY);
        if (alreadyNotifiedRunId === latestRunId) {
          return;
        }

        const startedAt = new Date(latestRun.started_at).getTime();
        if (Number.isNaN(startedAt) || (Date.now() - startedAt) > ZOMBIE_NOTIFICATION_MAX_AGE_MS) {
          window.localStorage.setItem(ZOMBIE_NOTIFICATION_STORAGE_KEY, latestRunId);
          return;
        }

        const summary = latestRun?.metrics?.summary || {};
        const affectedCount =
          Number(summary.phase1_reminder_1h || summary.phase1_changecard || 0) +
          Number(summary.phase2_changecard_24h || summary.phase2_reminder_24h || summary.phase2_reminder || 0) +
          Number(summary.phase3_reminder_48h || 0) +
          Number(summary.phase4_regularized || summary.phase3_regularized || 0);

        if (!affectedCount) {
          window.localStorage.setItem(ZOMBIE_NOTIFICATION_STORAGE_KEY, latestRunId);
          return;
        }

        window.localStorage.setItem(ZOMBIE_NOTIFICATION_STORAGE_KEY, latestRunId);

        if (Notification.permission === 'granted') {
          new Notification('Suscripciones zombie detectadas', {
            body: `El último cron procesó ${affectedCount} caso(s) de suscripciones pendientes.`,
            tag: `zombie-subscriptions-${latestRunId}`,
          });
        }
      } catch (_) {
        return;
      }
    };

    checkZombieSubscriptionAlerts();

    const intervalId = window.setInterval(checkZombieSubscriptionAlerts, 60000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex w-full overflow-hidden">

        {/* ── Sidebar ──────────────────────────────────── */}
        <AppSidebar />

        <SidebarInset className="flex flex-col flex-1 min-w-0 overflow-hidden">

          {/* ── Header glassmorphism ──────────────────── */}
          <header className="yool-header-glass w-full px-4 lg:px-6 gap-3 shrink-0">

            {/* Trigger móvil */}
            <SidebarTrigger className="flex-shrink-0 text-slate-500 hover:text-[#007D6E] transition-colors" />

            {/* Separator */}
            <div className="h-5 w-px bg-slate-200 dark:bg-slate-700 flex-shrink-0" />

            {/* Breadcrumb */}
            <div className="flex-1 min-w-0">
              <Breadcrumb />
            </div>

            {/* Derecha: search ⌘K + theme + user */}
            <div className="flex items-center gap-2 flex-shrink-0">

              {/* ⌘K search pill */}
              <button
                className="yool-search-pill hidden sm:flex"
                onClick={() => setSearchOpen(true)}
                aria-label="Abrir búsqueda (⌘K)"
              >
                <Search className="h-3.5 w-3.5" />
                <span>Buscar…</span>
                <span className="flex items-center gap-0.5 ml-1 opacity-60">
                  <Command className="h-3 w-3" />
                  <span className="text-[10px] font-mono">K</span>
                </span>
              </button>

              {/* Botón search móvil (solo ícono) */}
              <button
                className="sm:hidden p-1.5 rounded-md text-slate-500 hover:text-[#007D6E] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                onClick={() => setSearchOpen(true)}
                aria-label="Buscar"
              >
                <Search className="h-4 w-4" />
              </button>

              <ThemeToggle className="p-1.5 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800 transition-colors" />

              <UserButton afterSignOutUrl="/" />
            </div>
          </header>

          {/* ── Main content ─────────────────────────── */}
          {/* `overflow-y-auto` + `overflow-x-hidden` y NO `overflow-auto`: el atajo habilita
              las dos direcciones, y la horizontal es justamente la que no debe existir. */}
          <main className="flex-1 p-6 overflow-y-auto overflow-x-hidden min-w-0">
            {children}
          </main>
        </SidebarInset>
      </div>

      {/* ⌘K Command palette */}
      <CommandSearch open={searchOpen} onClose={closeSearch} />
    </SidebarProvider>
  );
}
